/**
 * Serves the app's static config JSON (self-service builders, side / top nav)
 * from the bundle instead of the network.
 *
 * The builders read their layout from `assets/self-service-config/*.json` —
 * a shell plus one file per chart family — and the nav from
 * `assets/side-navbar-config/*.json`. Fetched, that is ~20 requests on every
 * page open (304s still cost a round trip each). The same files are imported
 * here at build time and answered from memory, so none of the services that
 * read them change and none of them hit the network.
 *
 * Editing one of these JSON files still applies: `ng serve` rebuilds on the
 * change, and a production build bundles the current version. A config URL
 * not listed here falls through to a normal request.
 */

import { HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { of } from 'rxjs';

import selfServiceConfigChartBuilder from '../../../public/assets/self-service-config/chart-builder.json';
import selfServiceConfigChartsBarColumn from '../../../public/assets/self-service-config/charts/bar-column.json';
import selfServiceConfigChartsDrillDownTable from '../../../public/assets/self-service-config/charts/drill-down-table.json';
import selfServiceConfigChartsHorizontalBar from '../../../public/assets/self-service-config/charts/horizontal-bar.json';
import selfServiceConfigChartsInputControl from '../../../public/assets/self-service-config/charts/input-control.json';
import selfServiceConfigChartsKpiCard from '../../../public/assets/self-service-config/charts/kpi-card.json';
import selfServiceConfigChartsLineArea from '../../../public/assets/self-service-config/charts/line-area.json';
import selfServiceConfigChartsNestedTable from '../../../public/assets/self-service-config/charts/nested-table.json';
import selfServiceConfigChartsPopup from '../../../public/assets/self-service-config/charts/popup.json';
import selfServiceConfigChartsPieComposition from '../../../public/assets/self-service-config/charts/pie-composition.json';
import selfServiceConfigChartsScatterBubble from '../../../public/assets/self-service-config/charts/scatter-bubble.json';
import selfServiceConfigChartsTableMatrix from '../../../public/assets/self-service-config/charts/table-matrix.json';
import selfServiceConfigChartsWaterfall from '../../../public/assets/self-service-config/charts/waterfall.json';
import selfServiceConfigDashboardBuilder from '../../../public/assets/self-service-config/dashboard-builder.json';
import selfServiceConfigDataBuilder from '../../../public/assets/self-service-config/data-builder.json';
import selfServiceConfigDesignBuilder from '../../../public/assets/self-service-config/design-builder.json';
import selfServiceConfigDvmLayoutBuilder from '../../../public/assets/self-service-config/dvm-layout-builder.json';
import selfServiceConfigDvmPublishedView from '../../../public/assets/self-service-config/dvm-published-view.json';
import selfServiceConfigKaveriGenerateBuilder from '../../../public/assets/self-service-config/kaveri-generate-builder.json';
import selfServiceConfigRuleBuilder from '../../../public/assets/self-service-config/rule-builder.json';
import sideNavbarConfigNestedConfigExample from '../../../public/assets/side-navbar-config/nested-config-example.json';
import sideNavbarConfigSidenavConfig from '../../../public/assets/side-navbar-config/sidenav.config.json';
import sideNavbarConfigTopnavConfig from '../../../public/assets/side-navbar-config/topnav.config.json';

/** Path under `assets/` → file contents. */
const BUNDLED_CONFIG: Readonly<Record<string, unknown>> = {
  'self-service-config/chart-builder.json': selfServiceConfigChartBuilder,
  'self-service-config/charts/bar-column.json': selfServiceConfigChartsBarColumn,
  'self-service-config/charts/drill-down-table.json': selfServiceConfigChartsDrillDownTable,
  'self-service-config/charts/horizontal-bar.json': selfServiceConfigChartsHorizontalBar,
  'self-service-config/charts/input-control.json': selfServiceConfigChartsInputControl,
  'self-service-config/charts/kpi-card.json': selfServiceConfigChartsKpiCard,
  'self-service-config/charts/line-area.json': selfServiceConfigChartsLineArea,
  'self-service-config/charts/nested-table.json': selfServiceConfigChartsNestedTable,
  'self-service-config/charts/popup.json': selfServiceConfigChartsPopup,
  'self-service-config/charts/pie-composition.json': selfServiceConfigChartsPieComposition,
  'self-service-config/charts/scatter-bubble.json': selfServiceConfigChartsScatterBubble,
  'self-service-config/charts/table-matrix.json': selfServiceConfigChartsTableMatrix,
  'self-service-config/charts/waterfall.json': selfServiceConfigChartsWaterfall,
  'self-service-config/dashboard-builder.json': selfServiceConfigDashboardBuilder,
  'self-service-config/data-builder.json': selfServiceConfigDataBuilder,
  'self-service-config/design-builder.json': selfServiceConfigDesignBuilder,
  'self-service-config/dvm-layout-builder.json': selfServiceConfigDvmLayoutBuilder,
  'self-service-config/dvm-published-view.json': selfServiceConfigDvmPublishedView,
  'self-service-config/kaveri-generate-builder.json': selfServiceConfigKaveriGenerateBuilder,
  'self-service-config/rule-builder.json': selfServiceConfigRuleBuilder,
  'side-navbar-config/nested-config-example.json': sideNavbarConfigNestedConfigExample,
  'side-navbar-config/sidenav.config.json': sideNavbarConfigSidenavConfig,
  'side-navbar-config/topnav.config.json': sideNavbarConfigTopnavConfig,
};

export const bundledConfigInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.method !== 'GET') return next(request);
  const path = request.url.split('?')[0];
  const at = path.lastIndexOf('assets/');
  const body = at >= 0 ? BUNDLED_CONFIG[path.slice(at + 'assets/'.length)] : undefined;
  if (body === undefined) return next(request);
  // A copy per caller: a consumer that edits its config must not edit everyone's.
  return of(new HttpResponse({ status: 200, url: request.url, body: structuredClone(body) }));
};
